import { z } from "zod";

/**
 * Shared DTO cho Admin Risk Review Queue (US-AE12.F1-0042 · @UC9110 risk-review-queue,
 * @UC9113 transaction-risk-signals). MỘT nguồn sự thật cho api (S02), worker feeding (S05),
 * admin web (S04).
 *
 * Mục = tín hiệu rủi ro (velocity gom tiền / tạo nhóm hàng loạt / member spike) do worker feeding từ
 * DỮ LIỆU THẬT → Ops triage: dismiss / investigate / action. action=suspend nối AF4.3 suspend group
 * (KHÔNG rebuild freeze). item chỉ decide MỘT LẦN (append-only về bản chất).
 *
 * BẤT BIẾN (invariants):
 * - T&S NHẸ (story:1760): KHÔNG field KYC/danh tính — chỉ tín hiệu + quyết định + audit trail.
 * - G2: KHÔNG field ghi/sửa tiền — chỉ theo dõi/quyết định. Giá trị tín hiệu (nếu có) = minor string.
 * - C2 no-PII thô: targetId là con trỏ (id) — KHÔNG tên/email/phone. Audit no-PII.
 * - `.strict()` cho body decide — chống over-post/field lạ. reason bắt buộc non-empty (AC2).
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin + mock resources.ts risk)
// ============================================================

/** Đối tượng tín hiệu (mock resources.ts risk trỏ group hoặc user). KHÔNG bịa loại ngoài story. */
export const riskTargetTypeSchema = z.enum(["group", "user"]);
export type RiskTargetType = z.infer<typeof riskTargetTypeSchema>;

/**
 * Loại tín hiệu rủi ro (EPICS:269 — "nhóm gom > X/tháng, tạo nhóm hàng loạt, member tăng vọt").
 * Worker S05 CHỈ feed từ nguồn dữ liệu THẬT (velocity/mass-creation/member-spike) — KHÔNG bịa signal.
 */
export const riskSignalSchema = z.enum([
  "monthly_collect_over", // nhóm gom tiền vượt ngưỡng/tháng (velocity)
  "mass_group_creation", // user tạo nhiều nhóm trong cửa sổ ngắn
  "member_spike", // member 1 nhóm tăng vọt % trong cửa sổ ngắn
]);
export type RiskSignal = z.infer<typeof riskSignalSchema>;

/** Mức rủi ro (khớp mock resources.ts score: high/medium/low + badge STATUS). */
export const riskScoreSchema = z.enum(["high", "medium", "low"]);
export type RiskScore = z.infer<typeof riskScoreSchema>;

/** Trạng thái vòng đời mục review (khớp badge STATUS admin). pending = mới feed; investigating = đang
 * điều tra; dismissed = bỏ qua; actioned = đã hành động (cảnh báo/đình chỉ). */
export const riskStatusSchema = z.enum(["pending", "investigating", "dismissed", "actioned"]);
export type RiskStatus = z.infer<typeof riskStatusSchema>;

/**
 * Quyết định triage (AC2). dismiss = bỏ qua (→ dismissed). investigate = đánh dấu điều tra
 * (→ investigating). action = hành động: đình chỉ target (→ actioned, nối AF4.3 suspend group).
 */
export const riskDecisionSchema = z.enum(["dismiss", "investigate", "action"]);
export type RiskDecision = z.infer<typeof riskDecisionSchema>;

// ============================================================
// Risk review item (list)
// ============================================================

/**
 * 1 dòng mục review trên board. targetId = id (KHÔNG PII thô — resolve tên ở UI ref). decision/reason/
 * decidedBy/decidedAt null khi pending. `detail` = mô tả ngắn tín hiệu (KHÔNG PII/tiền thô).
 */
export const riskReviewItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    targetType: riskTargetTypeSchema,
    targetId: z.string(),
    /**
     * Display-name target resolve server-side (T01 · @UC9135): group → tên nhóm; user →
     * username(social)→email→phone. null khi không resolve được (mồ côi) → UI fallback id-slice.
     */
    targetName: z.string().nullable(),
    signal: riskSignalSchema,
    detail: z.string(),
    score: riskScoreSchema,
    status: riskStatusSchema,
    decision: riskDecisionSchema.nullable(),
    reason: z.string().nullable(),
    decidedBy: z.string().nullable(),
    decidedAt: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export type RiskReviewItem = z.infer<typeof riskReviewItemSchema>;

/** Envelope list: items + cursor pageInfo `{ data, pageInfo }` (nhất quán contracts hiện hữu). */
export const riskReviewListResponseSchema = z
  .object({
    data: z.array(riskReviewItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type RiskReviewListResponse = z.infer<typeof riskReviewListResponseSchema>;

// ============================================================
// Query (GET /admin/risk/queue)
// ============================================================

export const RISK_REVIEW_LIST_MAX_LIMIT = 100;

/** Query list — filter status/score, cursor + limit. `.strict()` chặn param lạ. */
export const riskReviewListQuerySchema = z
  .object({
    status: riskStatusSchema.optional(),
    score: riskScoreSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(RISK_REVIEW_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type RiskReviewListQuery = z.infer<typeof riskReviewListQuerySchema>;

// ============================================================
// Decide (POST /admin/risk/queue/:id/decide)
// ============================================================

export const RISK_DECISION_REASON_MIN_LENGTH = 1;
export const RISK_DECISION_REASON_MAX_LENGTH = 2000;

/**
 * Body quyết định triage (AC2). decision ∈ {dismiss,investigate,action}; reason BẮT BUỘC non-empty
 * (mọi quyết định phải có lý do — audit). `.strict()`. KHÔNG field ghi/sửa tiền (G2). action=suspend
 * WIRE tới AF4.3 ở service (KHÔNG rebuild freeze ở đây).
 */
export const decideRiskReviewSchema = z
  .object({
    decision: riskDecisionSchema,
    reason: z
      .string()
      .trim()
      .min(RISK_DECISION_REASON_MIN_LENGTH)
      .max(RISK_DECISION_REASON_MAX_LENGTH),
  })
  .strict();

export type DecideRiskReviewBody = z.infer<typeof decideRiskReviewSchema>;

// ============================================================
// Risk scoring thresholds (config-driven qua system_settings) — MỘT nguồn sự thật cho
// api reader (S02) + worker feeding (S05). [Decision Record: sensible-default version-1 product,
// SD-9 — agent tự chốt, PO backfill docs sau]. KHÔNG bịa số báo động ngoài ví dụ story (EPICS:269).
// ============================================================

/**
 * Key system_settings cho ngưỡng risk + default sensible. Giá trị đọc động lúc feeding (S05) —
 * đổi qua endpoint system-settings (SUPER_ADMIN + audit) có hiệu lực lần quét sau (AC3).
 *  - monthly_collect_threshold_minor: nhóm gom tiền vượt ngưỡng/tháng (velocity). Money = BigInt minor.
 *  - mass_group_creation_count / _window_hours: user tạo ≥ N nhóm trong cửa sổ giờ.
 *  - member_spike_pct / _window_days: member 1 nhóm tăng ≥ X% trong cửa sổ ngày.
 */
export const RISK_THRESHOLD_KEYS = {
  monthlyCollectThresholdMinor: "trust.risk.monthly_collect_threshold_minor",
  massGroupCreationCount: "trust.risk.mass_group_creation_count",
  massGroupCreationWindowHours: "trust.risk.mass_group_creation_window_hours",
  memberSpikePct: "trust.risk.member_spike_pct",
  memberSpikeWindowDays: "trust.risk.member_spike_window_days",
} as const;

/**
 * Default sensible version-1 (KHÔNG bịa số báo động — bám ví dụ story). Đổi CHỈ qua system-settings.
 *  - 50,000,000 minor = 50tr VND/tháng (ví dụ story "gom 97.5tr/tháng" > ngưỡng này).
 *  - 5 nhóm / 48h (ví dụ story "tạo 7 nhóm trong 48h" > ngưỡng này).
 *  - +100% member trong 7 ngày (ví dụ story "member tăng vọt +120% tuần" > ngưỡng này).
 */
export const RISK_THRESHOLD_DEFAULTS = {
  monthlyCollectThresholdMinor: "50000000", // minor string (BigInt-safe qua JSON)
  massGroupCreationCount: 5,
  massGroupCreationWindowHours: 48,
  memberSpikePct: 100,
  memberSpikeWindowDays: 7,
} as const;
